import { audioCtx } from "./synth";

const safeDisconnect = (node) => {
  try { node.disconnect(); } catch (e) {
    if (e?.name !== "InvalidAccessError") console.warn("Audio node disconnect failed", e); // InvalidAccessError = was not connected
  }
};

const impulse = (c, seconds = 2.4) => {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, len, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.6);
  }
  return buf;
};

// Every voice (remote mics, instruments, the shared track player) -> channel EQ -> fader -> master, with reverb/echo sends.
export class MixEngine {
  constructor() {
    const c = (this.ctx = audioCtx());
    this.buf = new Float32Array(512);
    this.master = c.createGain();
    this.masterAn = c.createAnalyser();
    this.masterAn.fftSize = 512;
    this.recordDest = c.createMediaStreamDestination();
    this.connectOut();

    this.reverb = c.createConvolver();
    this.reverb.buffer = impulse(c);
    this.revReturn = c.createGain();
    this.reverb.connect(this.revReturn).connect(this.master);
    this.delay = c.createDelay(2);
    this.feedback = c.createGain();
    this.delay.connect(this.feedback).connect(this.delay);
    this.delay.connect(this.master);

    this.channels = Array.from({ length: 12 }, () => {
      const input = c.createGain();
      const low = Object.assign(c.createBiquadFilter(), { type: "lowshelf" });
      low.frequency.value = 120;
      const mid = Object.assign(c.createBiquadFilter(), { type: "peaking" });
      mid.frequency.value = 1000;
      mid.Q.value = 0.9;
      const high = Object.assign(c.createBiquadFilter(), { type: "highshelf" });
      high.frequency.value = 8000;
      const g = c.createGain();
      const an = c.createAnalyser();
      an.fftSize = 512;
      const rev = c.createGain();
      const echo = c.createGain();
      rev.gain.value = 0;
      echo.gain.value = 0;
      input.connect(low).connect(mid).connect(high).connect(g);
      g.connect(an);
      g.connect(this.master);
      g.connect(rev).connect(this.reverb);
      g.connect(echo).connect(this.delay);
      return { input, low, mid, high, g, an, rev, echo };
    });
    this.direct = c.createGain();
    this.direct.connect(this.master);
    this.player = new Audio();
    this.player.crossOrigin = "anonymous";
    this.playerKey = null;
    this.sources = new Map([["__player", { node: c.createMediaElementSource(this.player), target: null }]]);
    this.state = null;
  }

  addTrack(key, track) {
    if (this.sources.get(key)?.track === track) return;
    this.removeSource(key);
    const ms = new MediaStream([track]);
    const el = new Audio();
    el.srcObject = ms;
    el.muted = true; // Chrome needs the element to keep remote WebRTC audio flowing
    el.play().catch(() => {});
    this.sources.set(key, { node: this.ctx.createMediaStreamSource(ms), el, track, target: null });
    this.route();
  }

  addNode(key, node) {
    if (this.sources.get(key)?.node === node) return;
    this.removeSource(key);
    this.sources.set(key, { node, target: null });
    this.route();
  }

  removeSource(key) {
    const s = this.sources.get(key);
    if (!s || key === "__player") return;
    safeDisconnect(s.node);
    if (s.el) s.el.srcObject = null;
    this.sources.delete(key);
  }

  keys() { return [...this.sources.keys()].filter((k) => k !== "__player"); }

  apply(mixer) { this.state = mixer; this.route(); this.gains(); }

  route() {
    const chs = this.state?.channels || [];
    for (const [key, s] of this.sources) {
      const k = key === "__player" ? this.playerKey : key;
      const idx = k ? chs.findIndex((c) => c.source === k) : -1;
      const target = idx >= 0 ? idx : "direct";
      if (s.target === target) continue;
      safeDisconnect(s.node);
      s.node.connect(idx >= 0 ? this.channels[idx].input : this.direct);
      s.target = target;
    }
  }

  gains() {
    const st = this.state || {};
    const chs = st.channels || [];
    const anySolo = chs.some((c) => c.solo);
    const t = this.ctx.currentTime;
    const set = (p, v) => p.setTargetAtTime(v, t, 0.03);
    chs.forEach((c, i) => {
      const n = this.channels[i];
      set(n.g.gain, c.mute || (anySolo && !c.solo) ? 0 : c.volume);
      set(n.low.gain, c.eq_low || 0);
      set(n.mid.gain, c.eq_mid || 0);
      set(n.high.gain, c.eq_high || 0);
      set(n.rev.gain, c.rev || 0);
      set(n.echo.gain, c.echo || 0);
    });
    set(this.direct.gain, anySolo ? 0 : 0.8);
    set(this.master.gain, st.master ?? 0.9);
    set(this.revReturn.gain, st.rev_return ?? 0.7);
    set(this.delay.delayTime, st.echo_time ?? 0.35);
    set(this.feedback.gain, st.echo_feedback ?? 0.35);
  }

  level(an) {
    an.getFloatTimeDomainData(this.buf);
    let s = 0;
    for (let i = 0; i < this.buf.length; i++) s += this.buf[i] * this.buf[i];
    return Math.min(1, Math.sqrt(s / this.buf.length) * 3.2);
  }

  // Walks a test tone through channels 1-12 (0.45s each) so every meter and the master light up in turn.
  soundcheck(onStep) {
    const c = this.ctx;
    c.resume();
    const step = 0.45;
    const t0 = c.currentTime + 0.05;
    this.channels.forEach((ch, i) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.frequency.value = 440 * Math.pow(2, (i % 6) / 12);
      g.gain.setValueAtTime(0, t0 + i * step);
      g.gain.linearRampToValueAtTime(0.35, t0 + i * step + 0.02);
      g.gain.setValueAtTime(0.35, t0 + (i + 1) * step - 0.04);
      g.gain.linearRampToValueAtTime(0, t0 + (i + 1) * step);
      o.connect(g).connect(ch.input);
      o.start(t0 + i * step);
      o.stop(t0 + (i + 1) * step + 0.02);
      setTimeout(() => onStep?.(i), (0.05 + i * step) * 1000);
    });
    setTimeout(() => onStep?.(-1), (0.1 + 12 * step) * 1000);
  }

  setPlayerTrack(key, url) {
    this.playerKey = key;
    this.sources.get("__player").target = null;
    this.route();
    if (this.player.dataset.url !== url) {
      this.player.dataset.url = url;
      this.player.src = url;
    }
  }

  // Idempotent: safe to call again after dispose() (React StrictMode re-mounts effects in dev).
  connectOut() {
    this.disconnectOut();
    this.master.connect(this.masterAn);
    this.master.connect(this.ctx.destination);
    this.master.connect(this.recordDest);
  }

  disconnectOut() { safeDisconnect(this.master); }

  dispose() {
    this.keys().forEach((k) => this.removeSource(k));
    this.player.pause();
    this.disconnectOut();
  }
}
